/**
 * Everything the table renders that did not come from the schema.
 *
 * `myHand` is the only place in the client where cards live, and it is only
 * ever written from the `hand.private` message — the one channel the server
 * addresses to a single seat (backend.md §3.2). Nothing copies it anywhere a
 * selector for another seat could read it.
 */

import type { StateCreator } from 'zustand';

import type { Slices, TableSlice } from './types';

const MAX_SELECTED = 4;

export const createTableSlice: StateCreator<Slices, [], [], TableSlice> = (set, get) => ({
  table: {
    myHand: [],
    myEval: null,
    legal: [],
    legalSeat: null,
    deadlineAt: 0,
    reserveMs: 0,
    selected: [],
    reveal: null,
    over: null,
    senas: [],
    bubbles: [],
    substituted: [],
    pendingFlip: null,
    flipVersion: 0,

    setLegal: (legalSeat, legal, deadlineAt, reserveMs) => {
      set((s) => ({ table: { ...s.table, legal, legalSeat, deadlineAt, reserveMs } }));
    },

    // A new hand invalidates any lingering discard selection: the indices would
    // now point at different cards.
    setHand: (myHand, myEval) => {
      set((s) => ({ table: { ...s.table, myHand, myEval, selected: [] } }));
    },

    toggleSelected: (index) => {
      set((s) => {
        const has = s.table.selected.includes(index);
        const selected = has
          ? s.table.selected.filter((i) => i !== index)
          : s.table.selected.length >= MAX_SELECTED
            ? s.table.selected
            : [...s.table.selected, index].sort((a, b) => a - b);
        return { table: { ...s.table, selected } };
      });
    },

    clearSelected: () => {
      set((s) => ({ table: { ...s.table, selected: [] } }));
    },

    // The Flip handshake (frontend.md §3.3). The director captures *before* the
    // mutation that reparents cards; `<FlipRoot/>` consumes it in a layout
    // effect after React has committed, which is the only moment both the old
    // rects and the new tree exist.
    captureFlip: (pendingFlip) => {
      set((s) => ({ table: { ...s.table, pendingFlip, flipVersion: s.table.flipVersion + 1 } }));
    },

    consumeFlip: () => {
      const pending = get().table.pendingFlip;
      if (pending !== null) set((s) => ({ table: { ...s.table, pendingFlip: null } }));
      return pending;
    },

    reset: () => {
      set((s) => ({
        table: {
          ...s.table,
          myHand: [],
          myEval: null,
          legal: [],
          legalSeat: null,
          deadlineAt: 0,
          reserveMs: 0,
          selected: [],
          reveal: null,
          over: null,
          senas: [],
          bubbles: [],
          substituted: [],
          pendingFlip: null,
        },
      }));
    },
  },
});
